import JsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'

const LOGO_PATH = '/images/logos/newlogo.png'
const PAGE_MARGIN = 40

function addHeader(doc, serialNumber) {
  const pageWidth = doc.internal.pageSize.getWidth()

  doc.addImage(LOGO_PATH, 'PNG', 60, 15, 100, 9)

  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('Technician Inspection Report', pageWidth / 2, 22, { align: 'center' })

  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.text(`Serial Number: ${serialNumber}`, pageWidth / 2, 34, { align: 'center' })

  doc.setDrawColor(200, 200, 200)
  // doc.line(PAGE_MARGIN, 42, pageWidth - PAGE_MARGIN, 42)

  return 50
}

function addSectionTitle(doc, title, y) {
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text(title, PAGE_MARGIN, y)
  return y + 6
}

function buildHeaderRows(itemInfo, itemResults) {
  return [
    ['Original Specification', itemResults?.itemDescription || ''],
    ['Inspected', itemInfo?.is_processed ? 'INSPECTED' : 'NOT INSPECTED'],
    ['POST', itemInfo?.post_result?.toUpperCase() || 'NOT TESTED'],
    ['Company Grade', itemInfo?.grade || ''],
    ['Location', `${itemInfo?.locations?.name || ''} ${itemInfo?.usersLocation?.name || ''}`.trim()],
    ['Condition', itemInfo?.condition || ''],
    ['Part Number', itemInfo?.item?.part_number || ''],
    ['Status', itemInfo?.status || ''],
    ['Technician', itemResults?.technicianName || ''],
    ['Last Inspected At', itemInfo?.last_inspection_at ? moment(itemInfo.last_inspection_at).format('MMM DD, YYYY HH:mm') : ''],
  ]
}

function getCpuResult(itemResults) {
  const value = itemResults?.resultCPU ?? itemResults?.resultCpu ?? itemResults?.cpuResult
  return value || ''
}

function getBatteryTypes(itemInfo) {
  return itemInfo?.item?.battery_type?.split(',').map((type) => type.trim()) || []
}

function buildComputerRows(itemInfo, itemResults) {
  const r = itemResults
  const showKeyboardSection = itemInfo?.item?.category?.id !== 1 && itemInfo?.item?.category?.id !== 3
  const batteryTypes = getBatteryTypes(itemInfo)
  const hasInternalBattery = batteryTypes.includes('internal')
  const hasRemovableBattery = batteryTypes.includes('removable')
  const hasSecondRemovableBattery = batteryTypes.includes('removable2')

  const batteryRows = []

  if (hasInternalBattery) {
    batteryRows.push(['Internal Battery Serial Number', r.batterySerialNumber || ''])
    batteryRows.push(['Internal Battery Health', r.batteryPercentage != null ? `${r.batteryPercentage}%` : ''])
    batteryRows.push(['Internal Battery Result', r.resultBattery || ''])
  }

  if (hasRemovableBattery) {
    batteryRows.push(['Removable Battery Serial Number', r.batterySerialNumber2 || ''])
    batteryRows.push(['Removable Battery Health', r.batteryPercentage2 != null ? `${r.batteryPercentage2}%` : ''])
    batteryRows.push(['Removable Battery Result', r.resultBattery2 || ''])
  }

  if (hasSecondRemovableBattery) {
    batteryRows.push(['2nd Removable Battery Serial Number', r.batterySerialNumber3 || ''])
    batteryRows.push(['2nd Removable Battery Health', r.batteryPercentage3 != null ? `${r.batteryPercentage3}%` : ''])
    batteryRows.push(['2nd Removable Battery Result', r.resultBattery3 || ''])
  }

  const keyboardRows = showKeyboardSection
    ? [
        ['Mouse Pad', r.resultMousePad || ''],
        ['Keyboard', r.resultKeyboard || ''],
        ['Keyboard Type', r.keyboardType || ''],
        ['Keyboard Language', r.keyboardLanguage || ''],
      ]
    : []

  return [
    // Hardware
    ['CPU / Motherboard', getCpuResult(r)],
    ['CPU', r.cpuModel?.label || r.cpuModel || ''],
    ['Screen', r.resultScreen || ''],
    ['LCD Screen', r.resultLcdScreen || ''],
    ['Screen Type', r.screenType || ''],
    ['Graphics Card', r.resultGraphicsCard || ''],
    ['Graphics Card Model', r.graphicsCard || ''],
    ['Dedicated GPU', r.resultDedicatedGpu || ''],
    ['Dedicated GPU Model', r.dedicatedGpu || ''],
    ['USB Ports', r.resultUsb || ''],
    ['USB Ports Count', r.usbPorts || ''],
    ...keyboardRows,
    ['Keyboard Ports', r.resultKeyboardPorts || ''],
    ['WLAN', r.resultWlan || ''],
    ['Bluetooth', r.resultBluetooth || ''],
    ['Audio', r.resultAudio || ''],
    ['Camera', r.resultCamera || ''],
    ['Camera Type', r.cameraType || ''],
    ['Face Recognition', r.resultFaceRecognition || ''],
    ['Fingerprint', r.resultFingerprint || ''],
    ['Smart Card', r.resultSmartCard || ''],
    ['Smart Card Type', r.smartCardType || ''],
    ['Stylus', r.resultStylus || ''],
    ['Stylus Type', r.stylusType || ''],
    ['Barcode Reader', r.resultBarcodeReader || ''],
    ['RFID', r.resultRfid || ''],
    ['GPS', r.resultGps || ''],
    ['WWAN', r.resultWwan || ''],
    ['WWAN Model', r.wwanModel || ''],
    ['Optical Drive', r.resultOpticalDrive || ''],
    ['Diagnostic Utility', r.resultDiagnosticUtility || ''],
    // Storage
    ['Storage 1 Size', r.storageSize1 || ''],
    ['Storage 1 Serial Number', r.storageSerialNumber1 || ''],
    ['Storage 1 Health', r.storageHealth1 != null && r.storageHealth1 !== '' ? `${r.storageHealth1}%` : ''],
    ['Storage 1 Result', r.resultStorage1 || ''],
    ['Storage 2 Size', r.storageSize2 || ''],
    ['Storage 2 Serial Number', r.storageSerialNumber2 || ''],
    ['Storage 2 Health', r.storageHealth2 != null && r.storageHealth2 !== '' ? `${r.storageHealth2}%` : ''],
    ['Storage 2 Result', r.resultStorage2 || ''],
    ['Storage Extended', r.resultStorageExtended || ''],
    // RAM
    ['RAM 1 Size', r.ramSize1?.label || r.ramSize1 || ''],
    ['RAM 1 Serial Number', r.ramSerialNumber1 || ''],
    ['RAM 1 Result', r.resultRam1 || ''],
    ['RAM 2 Size', r.ramSize2?.label || r.ramSize2 || ''],
    ['RAM 2 Serial Number', r.ramSerialNumber2 || ''],
    ['RAM 2 Result', r.resultRam2 || ''],
    // Battery
    ...batteryRows,
    ['AC Adapter', r.acAdapter || ''],
    // Software
    ['Operating System', r.operatingSystem || ''],
    ['Operating System Result', r.resultOperatingSystem || ''],
    ['Windows Activated', r.windowsActivated || ''],
    ['Windows Activation Result', r.resultWindowsActivation || ''],
    ['Drivers Installed', r.resultDriversInstalled || ''],
    ['Recovery Media', r.resultRecoveryMedia || ''],
    ['CFC2 BIOS Update', r.resultCfc2BiosUpdate || ''],
    ['Hours', r.hours != null ? String(r.hours) : ''],
    ['Notes', r.notes || ''],
  ]
}

function buildKeyboardRows(itemResults) {
  const r = itemResults
  return [
    ['Keyboard Result', r.resultKeyboard || ''],
    ['Keyboard Type', r.keyboardType || ''],
    ['Keyboard Language', r.keyboardLanguage || ''],
    ['CPU / Motherboard', getCpuResult(r)],
    ['Mouse Pad', r.resultMousePad || ''],
    ['USB Ports Result', r.resultUsb || ''],
    ['USB Ports Count', r.usbPorts || ''],
    ['Other Ports Result', r.resultKeyboardPorts || ''],
    ['Battery Serial Number', r.batterySerialNumber || ''],
    ['Battery Health', r.batteryPercentage != null ? `${r.batteryPercentage}%` : ''],
    ['Battery Result', r.resultBattery || ''],
    ['Side Covers', r.resultCovers || ''],
    ['Screws', r.resultScrews || ''],
    ['Cracks', r.cracks || ''],
    ['Label Condition', r.labelCondition || ''],
    ['Dents & Scratches', r.dentsScratches || ''],
    ['Discoloration', r.discoloration || ''],
    ['Missing Parts', r.missingParts || ''],
    ['Latches & Hinges Damaged', r.latchesHingesDamaged || ''],
    ['Cleaned', r.cleaned || ''],
    ['Packaging', r.packaging || ''],
    ['Notes', r.notes || ''],
  ]
}

function buildBatteryRows(itemResults) {
  const r = itemResults
  return [
    ['Battery Health', r.batteryPercentage != null ? `${r.batteryPercentage}%` : ''],
    ['Battery Result', r.resultBattery || ''],
    ['Battery Report', r.resultBatteryReport || ''],
    ['Notes', r.notes || ''],
  ]
}

function buildStorageRows(itemResults) {
  const r = itemResults
  return [
    ['Storage Health', r.storageHealth1 != null ? `${r.storageHealth1}%` : ''],
    ['Notes', r.notes || ''],
  ]
}

function buildRamRows(itemResults) {
  const r = itemResults
  return [['Notes', r.notes || '']]
}

export function generateInspectionPDF(itemInfo, itemResults) {
  const doc = new JsPDF('p', 'pt', 'letter')
  const serialNumber = itemInfo?.serial_number?.toUpperCase() || ''

  let startY = addHeader(doc, serialNumber)

  startY = addSectionTitle(doc, 'Item Information', startY)

  autoTable(doc, {
    startY,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    head: [['Field', 'Value']],
    headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255], fontSize: 9 },
    body: buildHeaderRows(itemInfo, itemResults),
    styles: { fontSize: 9, overflow: 'linebreak', cellPadding: 4 },
    columnStyles: { 0: { cellWidth: 140, fontStyle: 'bold' } },
  })

  const categoryId = itemInfo?.item?.category?.id
  const subcategoryId = itemInfo?.item?.subcategory?.id

  let sectionTitle = 'Inspection Details'
  let rows = []

  if (categoryId === 4 && subcategoryId === 10) {
    sectionTitle = 'Keyboard Inspection'
    rows = buildKeyboardRows(itemResults)
  } else if (categoryId === 9 && subcategoryId === 4) {
    sectionTitle = 'Battery Inspection'
    rows = buildBatteryRows(itemResults)
  } else if (categoryId === 6) {
    sectionTitle = 'Storage Inspection'
    rows = buildStorageRows(itemResults)
  } else if ([7, 15].includes(categoryId)) {
    sectionTitle = 'RAM Inspection'
    rows = buildRamRows(itemResults)
  } else {
    sectionTitle = 'Computer Inspection'
    rows = buildComputerRows(itemInfo, itemResults)
  }

  const detailsY = doc.lastAutoTable.finalY + 16
  addSectionTitle(doc, sectionTitle, detailsY)

  autoTable(doc, {
    startY: detailsY + 6,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    head: [['Field', 'Value']],
    headStyles: { fillColor: [62, 177, 158], textColor: [255, 255, 255], fontSize: 9 },
    body: rows,
    styles: { fontSize: 9, overflow: 'linebreak', cellPadding: 4 },
    columnStyles: { 0: { cellWidth: 180, fontStyle: 'bold' } },
  })

  doc.save(`inspection-report-${serialNumber}-${moment().format('YYYY-MM-DD')}.pdf`)
}
